import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Role } from '../models/role';

@Injectable({
  providedIn: 'root'
})
export class SharedRoleService {
  private rolesSubject = new BehaviorSubject<Role[]>([]);
  public roles$ = this.rolesSubject.asObservable();

  // Default roles that match the permissions table
  private defaultRoles: Role[] = [
    // i18n-ignore
    { id: 1, name: 'Super Admin', description: 'Super Administrator with full access to all system features' },
    // i18n-ignore
    { id: 2, name: 'CEO', description: 'Chief Executive Officer with management privileges' },
    // i18n-ignore
    { id: 3, name: 'Process Team Leader', description: 'Team leader for process management' },
    // i18n-ignore
    { id: 4, name: 'CSSL', description: 'Client Service Senior Leader' },
    // i18n-ignore
    { id: 5, name: 'CSL', description: 'Client Service Leader' },
    // i18n-ignore
    { id: 6, name: 'Process Technician 1', description: 'Level 1 Process Technician' },
    // i18n-ignore
    { id: 7, name: 'Process Technician 2', description: 'Level 2 Process Technician' },
    // i18n-ignore
    { id: 8, name: 'Process Technician 3', description: 'Level 3 Process Technician' },
    // i18n-ignore
    { id: 9, name: 'Process Technician 4', description: 'Level 4 Process Technician' },
    // i18n-ignore
    { id: 10, name: 'Pool Admin', description: 'Pool Administrator with pool management privileges' },
    // i18n-ignore
    { id: 11, name: 'Pool', description: 'Pool member role' },
    // i18n-ignore
    { id: 12, name: 'Evaluation Admin', description: 'Evaluation Administrator with evaluation management privileges' },
    // i18n-ignore
    { id: 13, name: 'Evaluation', description: 'Evaluation role for performance assessments' },
    // i18n-ignore
    { id: 14, name: 'Promotion Admin', description: 'Promotion Administrator with promotion management privileges' },
    // i18n-ignore
    { id: 15, name: 'Promotion', description: 'Promotion role for career advancement' },
    // i18n-ignore
    { id: 16, name: 'Employee', description: 'Basic employee role with limited access' },
    // i18n-ignore
    { id: 17, name: 'Audit Log Admin', description: 'Audit Log Administrator with audit log read access' },
    // i18n-ignore
    { id: 18, name: 'Invoicing', description: 'Invoicing role for finance operational access' },
    // i18n-ignore
    { id: 19, name: 'Invoicing Admin', description: 'Invoicing administrator role for elevated finance actions' },
    // i18n-ignore
    { id: 20, name: 'Manager', description: 'Manager with management privileges' },
    // i18n-ignore
    { id: 21, name: 'Director', description: 'Director with management privileges' }
  ];

  // Role mappings for converting between long and short names (matches role-mgmt component)
  private roleMappings = [
    { longName: 'Super Admin', shortName: 'SA' },
    { longName: 'CEO', shortName: 'CEO' },
    { longName: 'Manager', shortName: 'MGR' },
    { longName: 'Director', shortName: 'DIR' },
    { longName: 'Process Team Leader', shortName: 'PTL' }, // i18n-ignore
    { longName: 'CSSL', shortName: 'CSSL' },
    { longName: 'CSL', shortName: 'CSL' },
    { longName: 'Process Technician 1', shortName: 'PT1' },
    { longName: 'Process Technician 2', shortName: 'PT2' },
    { longName: 'Process Technician 3', shortName: 'PT3' },
    { longName: 'Process Technician 4', shortName: 'PT4' },
    { longName: 'Pool Admin', shortName: 'PA' },
    { longName: 'Pool', shortName: 'P' },
    { longName: 'Evaluation Admin', shortName: 'EA' },
    { longName: 'Evaluation', shortName: 'E' },
    { longName: 'Promotion Admin', shortName: 'PRA' },
    { longName: 'Promotion', shortName: 'PR' },
    { longName: 'Employee', shortName: 'EMP' },
    { longName: 'Audit Log Admin', shortName: 'ALA' }, // i18n-ignore
    { longName: 'Invoicing', shortName: 'INV' },
    { longName: 'Invoicing Admin', shortName: 'IADM' }
  ];

  constructor() {
    // Initialize with default roles
    this.rolesSubject.next([...this.defaultRoles]);
  }

  /**
   * Get current roles
   */
  getRoles(): Role[] {
    return this.rolesSubject.value;
  }

  /**
   * Set roles (used by roles component)
   */
  setRoles(roles: Role[]): void {
    this.rolesSubject.next(roles);
  }

  /**
   * Add a new role
   */
  addRole(role: Role): void {
    const currentRoles = this.rolesSubject.value;
    const updatedRoles = [...currentRoles, role];
    this.rolesSubject.next(updatedRoles);
  }

  /**
   * Update an existing role
   */
  updateRole(updatedRole: Role): void {
    const currentRoles = this.rolesSubject.value;
    const index = currentRoles.findIndex(role => role.id === updatedRole.id);
    if (index !== -1) {
      const updatedRoles = [...currentRoles];
      updatedRoles[index] = updatedRole;
      this.rolesSubject.next(updatedRoles);
    }
  }

  /**
   * Remove a role
   */
  removeRole(roleId: number): void {
    const currentRoles = this.rolesSubject.value;
    const updatedRoles = currentRoles.filter(role => role.id !== roleId);
    this.rolesSubject.next(updatedRoles);
  }

  /**
   * Get short role name for permissions table
   */
  getShortRoleName(longName: string): string {
    const mapping = this.roleMappings.find(m => m.longName === longName);
    return mapping ? mapping.shortName : longName;
  }

  /**
   * Get long role name from short name
   */
  getLongRoleName(shortName: string): string {
    const mapping = this.roleMappings.find(m => m.shortName === shortName);
    return mapping ? mapping.longName : shortName;
  }

  /**
   * Get default permissions for a new role
   */
  getDefaultPermissionsForRole(roleName: string): string {
    if (roleName === 'Invoicing' || roleName === 'Invoicing Admin') {
      return '000000000000000000000000000011000';
    }

    // Default permissions for new roles (basic employee permissions)
    return '00000100000000000000100000000';
  }

  /**
   * Check if a role mapping exists
   */
  hasRoleMapping(roleName: string): boolean {
    return this.roleMappings.some(m => m.longName === roleName || m.shortName === roleName);
  }

  /**
   * Add a new role mapping for custom roles
   */
  addRoleMapping(longName: string, shortName: string): void {
    if (!this.hasRoleMapping(longName)) {
      this.roleMappings.push({ longName, shortName });
    }
  }

  /**
   * Get all role mappings
   */
  getRoleMappings() {
    return [...this.roleMappings];
  }
}
